import {
  calculateArrayTotals,
  type FieldHardware,
  type PvDcIsolatorEntry,
  type HardwareCheckSelection,
  type HardwareKind,
} from '@cer-autofill/shared';

export function arrayModuleIndexAfterRemoval(
  current: number | undefined,
  removedIndex: number,
): number | undefined {
  if (current === undefined) return undefined;
  if (current === removedIndex) return undefined;
  if (current > removedIndex) return current - 1;
  return current;
}

/**
 * Drop one hardware row from the draft. Returns null when the index is only
 * the empty placeholder, so a confirm on that placeholder changes nothing.
 * Module selection follows the rows that remain, and array totals are recalculated.
 */
export function hardwareWithoutRow(
  hardware: FieldHardware,
  kind: HardwareKind,
  index: number,
): FieldHardware | null {
  const rows = hardware[kind];
  if (!Number.isInteger(index) || index < 0 || index >= rows.length) return null;
  const next: FieldHardware = {
    ...hardware,
    [kind]: rows.filter((_, rowIndex) => rowIndex !== index),
  };
  if (kind === 'modules') {
    const arrayModuleIndex = arrayModuleIndexAfterRemoval(hardware.arrayModuleIndex, index);
    if (arrayModuleIndex === undefined) delete next.arrayModuleIndex;
    else next.arrayModuleIndex = arrayModuleIndex;
  }
  return calculateArrayTotals(next);
}

/**
 * Photo-check selections point at a row by position. Drop the removed row and
 * move later rows of that kind down one. `expectedRow` stays with the row.
 *
 * Ratings copied from an inverter onto a DC isolator live on that inverter
 * selection. Its index is the copy's source. Shifting the selection renumbers
 * the source; dropping it clears the copy. Leaving the old index in place
 * makes the save filter drop both the copied values and their source.
 */
export function reindexHardwareCheckSelections(
  selections: readonly HardwareCheckSelection[],
  kind: HardwareKind,
  removedIndex: number,
): HardwareCheckSelection[] {
  return selections.flatMap((selection) => {
    if (selection.kind !== kind) return [selection];
    if (selection.index === removedIndex) return [];
    if (selection.index > removedIndex) return [{ ...selection, index: selection.index - 1 }];
    return [selection];
  });
}

export function removeHardwareRowPrompt(kind: HardwareKind, index: number): string {
  const n = index + 1;
  switch (kind) {
    case 'inverters':
      return `Remove Inverter ${n}? Its make, model and ratings will be cleared from this draft.`;
    case 'modules':
      return `Remove Module ${n}? Its make, model and ratings will be cleared from this draft.`;
    case 'acIsolators':
      return `Remove AC isolator ${n}? Its rating and location will be cleared from this draft.`;
    case 'dcIsolators':
      return `Remove DC isolator ${n}? Its ratings and location will be cleared from this draft.`;
    case 'batteries':
      return `Remove Battery ${n}? Its details will be cleared from this draft.`;
  }
}

/** Visible draft defaults, saved only when the reviewer saves the edit. */
export function defaultDcIsolator(row: PvDcIsolatorEntry = {}): PvDcIsolatorEntry {
  return { ...row, location: row.location ?? 'Integrated', polarised: row.polarised ?? false };
}
